/* Interface publique française - Version production */
import { CollapsibleText } from '@/components/CollapsibleText';
import { ScrollableRow } from '@/components/ScrollableRow';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation, Trans } from 'react-i18next';
import { Header } from '@/components/Header';
import { SEO, breadcrumbJsonLd } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, MapPin } from 'lucide-react';
import { pickTranslation } from '@/i18n/pickTranslation';
import { ExcursionCard } from '@/components/ExcursionCard';
import { hasNoDeposit } from '@/lib/noDeposit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Note : la catégorie `scooter` est redirigée vers la marketplace dédiée
// (/scooters). Sur /services, on affiche uniquement les autres catégories
// chargées depuis MongoDB (transferts, excursions, chauffeur privé).

export const ServicesPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  // The URL is the single source of truth for the tab: links (home, menu, footer), back / forward, reload and
  // shared URLs all land on the right tab; /services without parameter stays the general catalogue (« Tous »).
  const selectedCategory = searchParams.get('category') || 'all';
  const requestedCategory = useRef(selectedCategory);
  useEffect(() => { requestedCategory.current = selectedCategory; }, [selectedCategory]);
  const [transportSubFilter, setTransportSubFilter] = useState(searchParams.get('transport_type') || 'all');

  // ✱ Unification du parcours Scooters : /services?category=scooter → /scooters
  // La marketplace scooters V1 est la seule expérience valide pour cette catégorie.
  const scooterRedirect = (searchParams.get('category') || '').toLowerCase() === 'scooter';

  useEffect(() => {
    if (scooterRedirect) return;
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, scooterRedirect]);

  if (scooterRedirect) {
    return <Navigate to="/scooters" replace />;
  }

  const fetchServices = async () => {
    setLoading(true);
    try {
      // "transport" category includes both "transfer" and "private_driver"
      let params = {};
      if (selectedCategory === 'transport') {
        // Don't filter by category — fetch both transfer and private_driver
        // We'll filter client-side based on transportSubFilter
      } else if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const { data } = await axios.get(`${API}/services`, { params });
      // La catégorie "scooter" est gérée exclusivement par /scooters.
      // Sur /services on écarte donc les scooters pour n'afficher que les
      // autres catégories (transferts, excursions, chauffeur privé).
      const nonScooterDb = (data || []).filter((s) => s.category !== 'scooter');

      // If "transport" category selected, filter for transfer + private_driver
      let filtered = nonScooterDb;
      if (selectedCategory === 'transport') {
        filtered = nonScooterDb.filter((s) => s.category === 'transfer' || s.category === 'private_driver');
      }

      setServices(filtered);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    // Radix tabs call this on mouse-down AND on focus: one history entry per real change.
    if (requestedCategory.current === category) return;
    requestedCategory.current = category;
    const params = new URLSearchParams(searchParams);
    if (category !== 'all') {
      params.set('category', category);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const filteredServices = services.filter((service) => {
    // Search query filter
    const matchesSearch = searchQuery
      ? service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    // Transport sub-filter (transfer / private_driver)
    if (selectedCategory === 'transport' && transportSubFilter !== 'all') {
      const matchesTransportType = service.category === transportSubFilter;
      return matchesSearch && matchesTransportType;
    }

    return matchesSearch;
  });

  const categoryLabels = {
    all: t('services.categoryAll'),
    scooter: t('services.categoryScooter'),
    transport: t('services.categoryTransport'),
    excursion: t('services.categoryExcursion')
  };

  const transportSubLabels = {
    all: t('services.transportSubAll'),
    transfer: t('services.transportSubTransfer'),
    private_driver: t('services.transportSubPrivateDriver')
  };

  return (
    <div className="min-h-screen bg-background" data-testid="services-page">
      <SEO
        title={t('seo.services.title')}
        description={t('seo.services.description')}
        canonical="https://ridalo.co/services"
        jsonLd={breadcrumbJsonLd([
          { name: t('seo.breadcrumb.home'), path: '/' },
          { name: t('seo.breadcrumb.services'), path: '/services' },
        ])}
      />
      <Header />

      <div className="pt-20 sm:pt-24 md:pt-28 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col">
          <div className="md:mb-4">
            <h1 className="text-2xl sm:text-5xl md:text-4xl font-bold tracking-tight text-primary mb-1 sm:mb-4 md:mb-1">
              {t('services.mobileTitle')}
            </h1>
            <p className="hidden md:block text-base text-ink-light" data-testid="services-short-intro">{t('services.shortIntro')}</p>
          </div>

          {/* Descriptive text. Phones: right under the title, one line + « Read more » (dialog). From md: AFTER the offers,
              in a calm block (the page's long title becomes its H2); complete, in the HTML, one copy. */}
          <div className="mb-2 sm:mb-8 md:order-last md:mb-0 md:mt-12 md:rounded-2xl md:border md:border-slate-200 md:bg-white md:p-6" data-testid="services-intro-block">
            <h2 className="hidden md:block text-xl font-semibold text-primary mb-2">{t('services.pageTitle')}</h2>
            <CollapsibleText title={t('services.mobileTitle')} className="text-base sm:text-lg md:text-base text-ink-light max-w-3xl" testId="services-intro">
              <Trans i18nKey="services.intro" components={{ transport: <Link to="/transport" className="text-accent font-medium hover:underline" data-testid="services-transport-link" /> }} />
            </CollapsibleText>
          </div>

          {/* Search + categories: one row from lg */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:mb-6">
          <div className="mb-3 sm:mb-8 lg:mb-0 lg:flex-1">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder={t('services.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-12"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="mb-3 sm:mb-6 lg:mb-0">
            <Tabs value={selectedCategory} onValueChange={handleCategoryChange}>
              <ScrollableRow testId="category-tabs-scroll">
              <TabsList className="bg-white border border-slate-200 p-1 rounded-full h-auto sm:h-9" data-testid="category-tabs">
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <TabsTrigger
                    key={key}
                    value={key}
                    className="rounded-full px-4 sm:px-6 py-2 max-sm:min-h-11 data-[state=active]:bg-accent data-[state=active]:text-white"
                    data-testid={`category-tab-${key}`}
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
              </ScrollableRow>
            </Tabs>
          </div>

          </div>

          {/* Transport Sub-Filter */}
          {selectedCategory === 'transport' && (
            <div className="mb-12">
              <Tabs value={transportSubFilter} onValueChange={setTransportSubFilter}>
                <TabsList className="bg-slate-50 border border-slate-200 p-1 rounded-lg" data-testid="transport-sub-tabs">
                  {Object.entries(transportSubLabels).map(([key, label]) => (
                    <TabsTrigger
                      key={key}
                      value={key}
                      className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
                      data-testid={`transport-sub-tab-${key}`}
                    >
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* Services Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent mx-auto"></div>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-12" data-testid="no-services">
              <p className="text-lg text-ink-light">{selectedCategory === 'excursion'
  ? t('services.excursionsComingSoon')
  : t('services.noServices')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8" data-testid="services-grid">
              {filteredServices.map((service) => service.category === 'excursion' ? (
                <ExcursionCard key={service.service_id} service={service} />
              ) : (
                <Link
                  to={service._href || `/services/${service.service_id}`}
                  key={service.service_id}
                  data-testid={`service-card-${service.service_id}`}
                >
                  <div className="card-service group cursor-pointer h-full flex flex-col">
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={service.images[0] || 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=600&q=80'}
                        alt={pickTranslation(service, 'title', i18n.language) || service.title}
                        width="640" height="416" loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-primary capitalize">
                        {service.category === 'scooter' ? t('services.badgeScooter')
                          : service.category === 'transfer' ? t('services.badgeTransfer')
                          : service.category === 'private_driver' ? t('services.badgePrivateDriver')
                          : t('services.badgeExcursion')}
                      </div>
                      {hasNoDeposit(service) && (
                        <div className="absolute top-3 right-3 bg-emerald-500 text-white rounded-full px-3 py-1 text-xs font-semibold shadow-sm" data-testid={`no-deposit-badge-${service.service_id}`}>
                          {t('transport.results.noDeposit')}
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-lg font-semibold text-primary mb-2">{pickTranslation(service, 'title', i18n.language) || service.title}</h3>
                      <p className="text-sm text-ink-light mb-3 line-clamp-2 flex-1">{pickTranslation(service, 'description', i18n.language) || service.description}</p>
                      <div className="flex items-center text-sm text-ink-light mb-3">
                        <MapPin className="w-4 h-4 mr-1 text-accent" />
                        {service.location}
                      </div>
                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-slate-100">
                        <div className="flex items-baseline">
                          <span className="text-xl font-bold text-primary">{service.price} THB</span>
                        </div>
                        <Button size="sm" className="btn-primary text-sm py-2 max-sm:min-h-11" data-testid={`book-button-${service.service_id}`}>
                          {t('services.bookButton')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
